import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

// ✅ Module-level (shared across ALL dialog-component instances)
// Kyunki ES module singleton hota hai, ye state sabhi instances ke beech share hoga
let openDialogCount = 0;
let savedScrollY = 0;

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

let startY = 0;

const recordTouchStart = (e) => {
  startY = e.touches[0].clientY;
};

const preventTouch = (e) => {
  const scroller = e.target.closest('.cart-drawer__items');
  if (!scroller) {
    e.preventDefault();
    return;
  }
  const { scrollTop, scrollHeight, clientHeight } = scroller;
  if (scrollHeight <= clientHeight) {
    e.preventDefault();
    return;
  }
  const currentY = e.touches[0].clientY;
  const goingDown = currentY > startY;
  const atTop = scrollTop <= 0;
  const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
  if ((atTop && goingDown) || (atBottom && !goingDown)) {
    e.preventDefault();
  }
};

let lockedY = 0;
const handleViewport = () => {
  const vv = window.visualViewport;
  if (!vv) return;

  document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
  window.scrollTo(0, lockedY);

  if (vv.height < window.innerHeight * 0.75) {
    showOverlay();
  } else {
    hideOverlay();
  }
};
let overlayEl = null;

const showOverlay = () => {
  if (overlayEl) return;
  overlayEl = document.createElement('div');
  overlayEl.className = 'ios-kb-overlay';
  document.body.appendChild(overlayEl);
};

const hideOverlay = () => {
  overlayEl?.remove();
  overlayEl = null;
};

// ✅ Body ko lock/unlock karne wale helpers — sirf iOS ke liye use honge.
// Android CSS ke `html:has(dialog[open]) { overflow: hidden }` se already
// handle ho raha hai, isliye JS-level fixed-positioning sirf iOS pe chalegi.
const lockBody = () => {
  if (!isIOS) return;
  requestAnimationFrame(() => {
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.overflow = 'hidden';
    document.body.style.top = `-${savedScrollY}px`;
  });
};

const unlockBody = () => {
  if (!isIOS) return;
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.overflow = '';
  window.scrollTo({ top: savedScrollY, behavior: 'instant' });
};

const addIOSTouchGuards = () => {
  if (!isIOS) return;
  document.addEventListener('touchstart', recordTouchStart, { passive: true });
  document.addEventListener('touchmove', preventTouch, { passive: false });
};

const removeIOSTouchGuards = () => {
  if (!isIOS) return;
  document.removeEventListener('touchstart', recordTouchStart);
  document.removeEventListener('touchmove', preventTouch);
};

export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    // ✅ Safety cleanup: Shopify section re-render (jaise add-to-cart ke
    // baad cart drawer refresh) is element ko DOM se remove kar sakta hai
    // JABKI dialog abhi open tha. Aisi situation me closeDialog() kabhi
    // call nahi hota, toh openDialogCount aur body-lock permanently stuck
    // reh jaate hain — yahi wo bug tha jo iOS pe "freeze" jaisa dikhta tha.
    if (this.refs.dialog?.open) {
      openDialogCount = Math.max(0, openDialogCount - 1);

      if (openDialogCount === 0) {
        unlockBody();
        removeIOSTouchGuards();
      }
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;
    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    // ✅ Sirf pehle dialog ke liye scroll position save karo aur body lock karo
    if (openDialogCount === 0) {
      savedScrollY = window.scrollY;
      lockBody();
    }

    if (openDialogCount === 0) {
      addIOSTouchGuards();
      this.refs.dialog.classList.remove('keyboard-open');
    }

    openDialogCount++;

    requestAnimationFrame(() => {
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;

    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // ✅ Sirf jab koi bhi dialog khula na ho tabhi body unlock karo
    openDialogCount = Math.max(0, openDialogCount - 1);

    if (openDialogCount === 0) {
      removeIOSTouchGuards();
      unlockBody();
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  #handleClick(event) {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  }

  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }
  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }
  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

// ✅ bfcache fix: Checkout jaise pages pe navigate karke "back" button se
// wapas aane par (jaise Bogos bundle add-to-cart -> checkout -> back),
// iOS/Android browsers page ko bfcache (back-forward cache) se restore
// karte hain instead of fresh reload. Agar navigate karte waqt drawer
// open tha, toh body pe laga hua position:fixed/overflow:hidden/top
// state waisa hi "frozen" restore ho jata hai — connectedCallback dobara
// nahi chalta, isliye purana fix trigger nahi hota. Isliye yahan
// explicitly reset karna padta hai jab bhi persisted page restore ho.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  openDialogCount = 0;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.overflow = '';

  removeIOSTouchGuards();

  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    dialog.classList.remove('dialog-closing');
    dialog.close();
  });
});